import React from 'react';
import PropTypes from 'prop-types';
import * as ReactRedux from 'react-redux';
import { bindActionCreators } from 'redux';
import {
  showNextTip,
  startTourPreviewOpen,
} from 'SelfServeTooltipTour/TooltipTourActions';
import { setStaticBuilderBackButton } from 'StaticBuilder/StaticBuilderActions';
import StaticBuilderSaveButton from 'StaticBuilder/StaticBuilderSaveButton';
import StaticBuilderBackButton from 'StaticBuilder/StaticBuilderBackButton';
import { isGetLinkType } from 'StaticBuilder/Utils';
import { ElementWithAnswerQuestionTooltip } from 'SelfServeRecipientBuilder/components';
import StaticBuilderFormattedFields from './StaticBuilderFormattedFields';
import StaticBuilderFormField from './StaticBuilderFormField';
import StaticBuilderAddAnotherButton from './StaticBuilderAddAnotherButton';
import StaticBuilderSignatureManagerContainer from './StaticBuilderSignatureManager';

// Event listener manipulation cannot be effectively stubbed for testing, but these wrappers can.
export const support = {
  addEventListener: (...args) => window.addEventListener(...args),
  removeEventListener: (...args) => window.removeEventListener(...args),
};

export class StaticBuilderForm extends React.Component {
  static getMemberTracking() {
    if (window.COOKIE.get('member') === '1') {
      return '-Member';
    }
    return '';
  }

  static onAddAdditonalGroup() {
    window.FormManager.addAnotherItem('groupMultiple');
  }

  static getBuilderStepFromUrlHash() {
    return window.location.hash.split('#')[1] ? parseInt(window.location.hash.split('#')[1], 10) : null;
  }

  constructor(props) {
    super(props);

    this.state = {
      isSaveButtonHidden: false,
      isSignatureAdded: false,
      shouldClosePreview: false,
    };
    this.onBack = this.onBack.bind(this);
    this.clickNextButton = this.clickNextButton.bind(this);
    this.onContinue = this.onContinue.bind(this);
    this.onSign = this.onSign.bind(this);
    this.isLastStep = this.isLastStep.bind(this);
    this.getSaveButtonContent = this.getSaveButtonContent.bind(this);
    this.getFormattedField = this.getFormattedField.bind(this);
    this.isSignatureType = this.isSignatureType.bind(this);
    this.hideSaveButton = this.hideSaveButton.bind(this);
    this.showSaveButton = this.showSaveButton.bind(this);
    this.setIsSignatureAdded = this.setIsSignatureAdded.bind(this);
    this.handleEnterPress = this.handleEnterPress.bind(this);
    this.isSignatureRequired = this.isSignatureRequired.bind(this);
    this.handlePreviewModalState = this.handlePreviewModalState.bind(this);
    this.onHashChange = this.onHashChange.bind(this);
    this.setCurrentFieldValue = this.setCurrentFieldValue.bind(this);
    this.trackWaiverSelection = this.trackWaiverSelection.bind(this);

    window.location.hash = this.props.onStep;

    this.currentFieldValue = null;
  }

  componentDidMount() {
    window.FormManager.hideUnnecessarySubSections('groupMultiple');
    if (this.props.inlineConditionals) {
      this.props.inlineConditionals();
    }
    support.addEventListener('keypress', this.handleEnterPress);
    support.addEventListener('hashchange', this.onHashChange);
  }

  componentDidUpdate() {
    if (this.props.isBackClicked) {
      this.onBack();
      this.props.setStaticBuilderBackButton(false);
    }
  }

  componentWillUnmount() {
    support.removeEventListener('keypress', this.handleEnterPress);
    support.removeEventListener('hashchange', this.onHashChange);
  }

  onHashChange() {
    const nextStep = StaticBuilderForm.getBuilderStepFromUrlHash();
    if ((nextStep >= 0) && (nextStep < this.props.onStep)) {
      this.props.loadPreviousStaticBuilderSection();
      window._track(
        `Section ${this.props.onStep + 1} | Browser Back Button`,
        `${this.props.documentType} | Builder${StaticBuilderForm.getMemberTracking()}`,
      );
    }
  }

  onBack() {
    this.showSaveButton();
    this.props.loadPreviousStaticBuilderSection();
    window._track(
      `Section ${this.props.onStep + 1} | Back Button`,
      `${this.props.documentType} | Builder${StaticBuilderForm.getMemberTracking()}`,
    );
  }

  onContinue() {
    this.handlePreviewModalState();
    this.props.saveAndContinue();

    let trackingSaveCategory = 'Save And Continue';
    if (this.isLastStep()) {
      if (this.props.isSelfServe && !this.props.isPreviewMode) {
        this.props.finishSelfServe();
        return;
      }
      trackingSaveCategory = 'Save And Export';
    }

    if (this.props.isSelfServe && this.props.isPreviewMode) {
      window._track('Save and Continue | Preview Modal', 'Template Builder');
    } else {
      window._track(
        `Section ${this.props.onStep + 1} | ${trackingSaveCategory}`,
        `${this.props.documentType} | Builder${StaticBuilderForm.getMemberTracking()}`,
      );
    }
  }

  onSign() {
    if (this.state.isSignatureAdded) {
      // save signature field
      this.handlePreviewModalState();
      this.props.saveAndSign(this.getFormattedField().field.id);
    }
    if (this.props.isSelfServe && this.props.isPreviewMode) {
      this.props.showNextTip();
    }
  }

  setCurrentFieldValue(value) {
    this.currentFieldValue = value;
  }

  getSaveButtonContent() {
    const { isSelfServe, isPreviewMode } = this.props;
    let buttonContent = 'Save & Continue';
    if (this.isLastStep()) {
      if (isSelfServe && isPreviewMode && this.state.shouldClosePreview) {
        buttonContent = 'Close Recipient View';
      } else if (!isPreviewMode && (this.isSignatureType() || isSelfServe)) {
        buttonContent = 'Finish & Send';
      } else if (!isSelfServe) {
        buttonContent = 'Save & Export';
      }
    }

    return buttonContent;
  }

  getFormattedField(index = 0) {
    return this.props.formattedFields.getAllFormattedFields()[index];
  }

  setIsSignatureAdded(state) {
    this.setState({
      isSignatureAdded: state,
    });
  }

  handlePreviewModalState() {
    const { isSelfServe, isPreviewMode } = this.props;
    if (isSelfServe && isPreviewMode && this.isLastStep()) {
      this.setState({
        shouldClosePreview: true,
      });
    }
  }

  trackWaiverSelection() {
    const { documentType, onStep } = this.props;
    const formattedField = this.getFormattedField();
    const fieldValue = this.currentFieldValue ||
      (formattedField && formattedField.field && formattedField.field.initialValue);
    const isGeneralForm = fieldValue === 'general_form';
    const isSpecificForm = fieldValue === 'specific_participant';
    if (isGetLinkType(documentType) && onStep === 3) {
      if (isGeneralForm) {
        window._track('Reuseable General Waiver', `${documentType} | Static-builder-member`);
      } else if (isSpecificForm) {
        window._track('Specific Waiver', `${documentType} | Static-builder-member`);
      }
    }
  }

  clickNextButton() {
    this.trackWaiverSelection();
    if (this.isLastStep() && this.props.showRequestSignature === false) {
      const { documentType } = this.props;
      window.exportStaticWithURL('No One | Request Signature Section', documentType, `export/pdf.php${window.location.search}`);
      const clearProgressBarInterval = setInterval(() => {
        if (document.getElementById('saveBarObj').style.visibility !== 'hidden') {
          $('#saveBarObj').fadeOut();
          clearInterval(clearProgressBarInterval);
        }
      }, 500);
      setTimeout(() => $('.saveButton').button('reset'), 1000);
      return;
    }
    if (this.props.isSelfServe && this.props.isPreviewMode) {
      this.props.showNextTip();
    }
    const { isSelfServe, finishSelfServe, isPreviewMode } = this.props;
    const isFinishAvailable = !isPreviewMode || (isPreviewMode && this.state.shouldClosePreview);
    if (this.isLastStep() && isSelfServe && isFinishAvailable) {
      finishSelfServe();
    } else {
      this.onContinue();
    }
  }

  // Capture the Enter button and treat it as clicking CTA
  handleEnterPress(e) {
    const { isSelfServe, finishSelfServe, isPreviewMode } = this.props;

    // Either it is a signature section and has been prepopulated or it's not a signature section
    const shouldContinue = this.state.isSignatureAdded || !this.isSignatureType();

    // Either it is not in preview mode or it is and Save & Continue has been pressed
    const isFinishAvailable = !isPreviewMode || (isPreviewMode && this.state.shouldClosePreview);

    if (isSelfServe && e.key === 'Enter' && shouldContinue) {
      e.preventDefault();

      if (isSelfServe && isPreviewMode) {
        this.props.showNextTip();
      }

      if (this.isSignatureRequired()) {
        this.onSign();
      } else if (this.isLastStep() && isFinishAvailable) {
        finishSelfServe();
      } else {
        this.onContinue();
      }
    }
  }

  isSignatureRequired() {
    const formattedField = this.getFormattedField();

    // Show the sign button instead of save and continue buttons
    return this.isSignatureType() &&
      formattedField &&
      formattedField.field &&
      formattedField.field.initialValue &&
      !(formattedField.field.initialValue.type ||
        formattedField.field.initialValue.draw);
  }

  isLastStep() {
    if (this.props.showRequestSignature === false) {
      return (this.props.sectionKeys.length - 1 === this.props.onStep);
    }
    return (
      ((window.secondPresence || !window.User.isLoggedIn ||
        this.props.isSelfServe) && !window.showRequestSignature) &&
      this.props.sectionKeys.length - 1 === this.props.onStep
    );
  }

  isSignatureType() {
    const formattedField = this.getFormattedField();

    return formattedField && formattedField.field && formattedField.field.type === 'signature';
  }

  hideSaveButton() {
    this.setState({
      isSaveButtonHidden: true,
    });
  }

  showSaveButton() {
    this.setState({
      isSaveButtonHidden: false,
    });
  }

  render() {
    return (
      <form id="staticBuilderForm" className="form-vertical static-builder-form">
        <fieldset>
          {
            this.props.formattedFields.getAllFormattedFields().map(
              (formattedField, i) => {
                if (formattedField.isMultiple) {
                  return (
                    <React.Fragment key={formattedField.fields[0].id}>
                      <div className="groupMultiple">
                        {
                          formattedField.fields.map(field => (
                            <StaticBuilderFormField
                              key={`groupMultiple-${field.id}`}
                              field={field}
                            />
                          ))
                        }
                      </div>
                      {
                        formattedField.isEndOfMultiple && (
                          <StaticBuilderAddAnotherButton
                            onAddAdditonalGroup={StaticBuilderForm.onAddAdditonalGroup}
                          />
                        )
                      }
                    </React.Fragment>
                  );
                }

                if (formattedField.field && formattedField.field.type === 'signature') {
                  return (
                    <ElementWithAnswerQuestionTooltip
                      targetElementType={formattedField.field.type}
                    >
                      <StaticBuilderSignatureManagerContainer
                        isSelfServe={this.props.isSelfServe}
                        key={formattedField.field.id}
                        onSign={this.onSign}
                        signatureActions={{
                          openCreatorAction: this.hideSaveButton,
                          closeCreatorAction: this.showSaveButton,
                          isSaveButtonHidden: this.state.isSaveButtonHidden,
                        }}
                        savedSignature={formattedField.field.initialValue || {}}
                        setIsSignatureAdded={this.setIsSignatureAdded}
                        isSignatureAdded={this.state.isSignatureAdded}
                      />
                    </ElementWithAnswerQuestionTooltip>
                  );
                }
                return (
                  <StaticBuilderFormField
                    key={formattedField.field.id}
                    field={formattedField.field}
                    fieldNumber={i}
                    setCurrentFieldValue={this.setCurrentFieldValue}
                  />
                );
              },
            )
          }

          <div className="form-actions">
            {this.props.onStep > 0 &&
              <StaticBuilderBackButton
                onBack={this.onBack}
              />
            }
            <StaticBuilderSaveButton
              buttonContent={this.getSaveButtonContent()}
              clickNextButton={this.clickNextButton}
              documentType={this.props.documentType}
              isHidden={this.state.isSaveButtonHidden}
              isLastStep={this.isLastStep()}
              isPreviewMode={this.props.isPreviewMode}
              isSelfServe={this.props.isSelfServe}
              isSignatureAdded={this.state.isSignatureAdded}
              onSign={this.onSign}
              shareKey={this.props.shareKey}
              showSignButton={this.isSignatureRequired()}
            />
          </div>
        </fieldset>
      </form>
    );
  }
}


StaticBuilderForm.defaultProps = {
  finishSelfServe: () => {},
  inlineConditionals: null,
  isBackClicked: false,
  isPreviewMode: false,
  isSelfServe: false,
  onStep: 0,
  sectionKeys: [],
  showNextTip: () => {},
  shareKey: '',
  showRequestSignature: true,
};

StaticBuilderForm.propTypes = {
  documentType: PropTypes.string.isRequired,
  finishSelfServe: PropTypes.func,
  formattedFields: PropTypes.instanceOf(StaticBuilderFormattedFields).isRequired,
  inlineConditionals: PropTypes.func,
  isBackClicked: PropTypes.bool,
  isPreviewMode: PropTypes.bool,
  isSelfServe: PropTypes.bool,
  loadPreviousStaticBuilderSection: PropTypes.func.isRequired,
  onStep: PropTypes.number,
  setStaticBuilderBackButton: PropTypes.func.isRequired,
  saveAndContinue: PropTypes.func.isRequired,
  saveAndSign: PropTypes.func.isRequired,
  sectionKeys: PropTypes.arrayOf(PropTypes.string),
  showNextTip: PropTypes.func,
  showRequestSignature: PropTypes.bool,
  shareKey: PropTypes.string,
};

const mapStateToProps = ({
  SelfServeBuilderReducer,
  StaticBuilderReducer,
}) => ({
  isBackClicked: StaticBuilderReducer.isBackClicked,
  isSampleState: SelfServeBuilderReducer.isSampleState,
});

const mapDispatchToProps = dispatch => (
  bindActionCreators({
    setStaticBuilderBackButton,
    showNextTip,
    startTourPreviewOpen,
  }, dispatch)
);

const StaticBuilderFormWithRedux = ReactRedux.connect(
  mapStateToProps,
  mapDispatchToProps,
)(StaticBuilderForm);

export default StaticBuilderFormWithRedux;



// WEBPACK FOOTER //
// ./src-react/StaticBuilder/StaticBuilderForm.jsx